/**
 * Turn any client-supplied timezone value into an id `Intl` accepts.
 *
 * Validity is decided by `Intl.DateTimeFormat`, never by membership of the
 * bootstrap continent list — that list has no `Etc/*` entries and no aliases,
 * so it would reject a legitimate older id.
 *
 * Decoding happens at most once. A value still percent-encoded after one pass
 * is client corruption, so it falls back instead of decoding again.
 *
 * @param {*} rawTimezone candidate value, any type
 * @param {string} fallbackTimezone id to use when `rawTimezone` cannot be resolved
 * @returns {string} a timezone id accepted by `Intl.DateTimeFormat`
 */
export function resolveTimezoneId(rawTimezone, fallbackTimezone) {
	if (typeof rawTimezone !== 'string') return fallbackTimezone;

	const trimmed = rawTimezone.trim();
	if (isValidTimezoneId(trimmed)) return trimmed;

	let decoded;
	try {
		decoded = decodeURIComponent(trimmed);
	} catch {
		return fallbackTimezone;
	}

	if (isValidTimezoneId(decoded)) return decoded;
	return fallbackTimezone;
}

function isValidTimezoneId(tz) {
	if (typeof tz !== 'string' || tz === '') return false;
	try {
		// eslint-disable-next-line no-new
		new Intl.DateTimeFormat('en', { timeZone: tz });
		return true;
	} catch {
		return false;
	}
}

export default resolveTimezoneId;
